import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { BrandImageCard } from '@components/Home/BrandImageCard';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { BookingStatus } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiTabs } from 'noddi-ui-web';
import { BookingsListTabs } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useBookingUserGroupId } from '../../../hooks/useBookingUserGroupId';
import { useCustomerAppPagination } from '../../../hooks/useCustomerAppPagination';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getTabsTranslations } from '../../../utils/commonTranslations';
import { BookingsList } from './BookingsList';

export const Bookings = () => {
  const { pagination, setPagination } = useCustomerAppPagination();
  const { currentBrand } = useCurrentBrand();

  const { getCurrentUserGroupId } = useAuthContext();
  const userGroupId = getCurrentUserGroupId() as number;
  const { bookingUserGroupId, isReady, error: bookingUserGroupError } = useBookingUserGroupId(userGroupId);

  // Fetch all bookings
  const {
    isPending: isAllBookingsPending,
    data: allBookings,
    error: allBookingsError,
    isFetching: isAllBookingsFetching
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId: bookingUserGroupId,
      statusIds: [BookingStatus.CONFIRMED, BookingStatus.COMPLETED, BookingStatus.ASSIGNED_TO_ROUTE],
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      brandDomains: [currentBrand]
    },
    queryConfig: { enabled: isReady }
  });

  // Fetch upcoming bookings
  const {
    isPending: isUpcomingBookingsPending,
    data: upcomingBookings,
    error: upcomingBookingsError,
    isFetching: isUpcomingBookingsFetching
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId: bookingUserGroupId,
      isUpcoming: true,
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      brandDomains: [currentBrand]
    },
    queryConfig: { enabled: isReady }
  });

  // Fetch previous/completed bookings
  const {
    isPending: isPreviousBookingsPending,
    data: previousBookings,
    error: previousBookingsError,
    isFetching: isPreviousBookingsFetching
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId: bookingUserGroupId,
      isCompleted: true,
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      brandDomains: [currentBrand]
    },
    queryConfig: { enabled: isReady }
  });

  if (!isReady || isAllBookingsPending || isUpcomingBookingsPending || isPreviousBookingsPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (bookingUserGroupError || allBookingsError || upcomingBookingsError || previousBookingsError) {
    return (
      <ApiErrorPageWithTrans
        error={[bookingUserGroupError, allBookingsError, upcomingBookingsError, previousBookingsError]}
      />
    );
  }

  if (allBookings.count === 0 && upcomingBookings.count === 0 && previousBookings.count === 0) {
    return (
      <ContentWrapperCustomerApp title={t`Your bookings`}>
        <div className='md:w-1/2'>
          <BrandImageCard customText={t`You don't have any bookings yet. Book your first service now!`} />
        </div>
      </ContentWrapperCustomerApp>
    );
  }

  const handlePaginationChange = ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => {
    setPagination({ pageIndex, pageSize, search: pagination.search });
  };

  return (
    <ContentWrapperCustomerApp title={t`Your bookings`} showNewBookingButton>
      <NoddiTabs<BookingsListTabs>
        translations={getTabsTranslations()}
        tabItems={[
          {
            label: t`All`,
            value: BookingsListTabs.ALL,
            count: allBookings.count,
            node: (
              <BookingsList
                bookings={allBookings}
                isFetching={isAllBookingsFetching}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                emptyStateMessage={t`You don't have any bookings yet.`}
              />
            )
          },
          {
            label: t`Upcoming`,
            value: BookingsListTabs.UPCOMING,
            count: upcomingBookings.count,
            node: (
              <BookingsList
                bookings={upcomingBookings}
                isFetching={isUpcomingBookingsFetching}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                emptyStateMessage={t`You don't have any upcoming bookings.`}
              />
            )
          },
          {
            label: t`Prior`,
            value: BookingsListTabs.PREVIOUS,
            count: previousBookings.count,
            node: (
              <BookingsList
                bookings={previousBookings}
                isFetching={isPreviousBookingsFetching}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                emptyStateMessage={t`You don't have any previous bookings.`}
              />
            )
          }
        ]}
        defaultValue={BookingsListTabs.ALL}
        keepExistingSearchParams
      />
    </ContentWrapperCustomerApp>
  );
};
